import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { useCart } from '@/hooks/useCart';
import { getProductImages } from '@/lib/productMedia';
import { trackAddToCart } from '@/lib/facebookPixel';
import { useStoreWhatsApp, buildWhatsAppUrl } from '@/hooks/useStoreWhatsApp';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { normalizeCurrency } from '@/lib/currency';
import { isDigitalProduct } from '@/lib/productType';
import { menuRoleLabel } from '@/lib/menuRole';
import { getOrganizationByCommerce } from '@/lib/zifekCore';
import ReviewsSection from '@/components/feature/ReviewsSection';
import RecommendedSection from '@/components/feature/RecommendedSection';
import FavoriteButton from '@/components/feature/FavoriteButton';
import { useThemePageTracking } from '@/hooks/useThemePageTracking';

interface ProductItem {
  id: number;
  name: string;
  description: string | null;
  price: number;
  discount_enabled: boolean;
  discount_price: number | null;
  media: unknown;
  category_id: number | null;
  status: string;
  stock: number | null;
  ville: string | null;
  pays: string | null;
  currency: string | null;
  owner: number | null;
  idcommerce: number | null;
  product_type: string | null;
  menu_role: string | null;
  digital_file_url: string | null;
  digital_file_name: string | null;
  digital_delivery_note: string | null;
}

export default function DetailProduitPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { tenant, isTenant, loading: tenantLoading } = useTenant();
  // Suivi de visite de la fiche produit publique (vues, IP, géo, provenance).
  useThemePageTracking('product', tenant?.id ?? null, isTenant && !!tenant);
  const { addToCart, currency } = useCart();
  const whatsapp = useStoreWhatsApp();

  const [product, setProduct] = useState<ProductItem | null>(null);
  const [categoryName, setCategoryName] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeImage, setActiveImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [added, setAdded] = useState(false);
  const [organizationId, setOrganizationId] = useState<number | null>(null);

  const monaie = normalizeCurrency(product?.currency || tenant?.monaie || currency || 'MAD');
  const { display, original } = useDisplayCurrency(monaie);

  useEffect(() => {
    if (!id) {
      setError('Produit introuvable');
      setLoading(false);
      return;
    }
    // Attente de la résolution du tenant : évite d'afficher un produit
    // avant de connaître le commerce courant (isolation par commerce).
    if (tenantLoading) return;
    setLoading(true);
    let query = supabase
      .from('product_items')
      .select('*')
      .eq('id', Number(id));
    // Sur la boutique d'un commerce, on n'accepte QUE ses propres produits.
    if (isTenant && tenant) query = query.eq('idcommerce', tenant.id);
    query
      .maybeSingle()
      .then(({ data, error: fetchError }) => {
        if (fetchError) setError(fetchError.message);
        else if (!data) setError('Produit introuvable');
        else setProduct(data as ProductItem);
      })
      .catch(() => setError('Erreur de chargement'))
      .finally(() => setLoading(false));
  }, [id, isTenant, tenant, tenantLoading]);

  useEffect(() => {
    if (product?.id) document.title = product.name;
  }, [product]);

  // Résolution de l'organisation du socle vendeur (pour les avis, spec 54).
  useEffect(() => {
    const commerceId = product?.idcommerce ?? product?.owner ?? null;
    if (!commerceId) {
      setOrganizationId(null);
      return;
    }
    let cancelled = false;
    getOrganizationByCommerce(Number(commerceId))
      .then((o) => { if (!cancelled) setOrganizationId(o?.id ?? null); })
      .catch(() => { if (!cancelled) setOrganizationId(null); });
    return () => { cancelled = true; };
  }, [product?.idcommerce, product?.owner]);

  useEffect(() => {
    if (product?.category_id) {
      supabase
        .from('product_categories')
        .select('name')
        .eq('id', product.category_id)
        .maybeSingle()
        .then(({ data }) => {
          if (data?.name) {
            setCategoryName(data.name);
          } else {
            // Repli : catégorie créée par le marchand (table `categorie`).
            supabase
              .from('categorie')
              .select('nom')
              .eq('id', product.category_id)
              .maybeSingle()
              .then(({ data: cat }) => {
                if (cat?.nom) setCategoryName(cat.nom);
              })
              .catch(() => { /* silent */ });
          }
        })
        .catch(() => { /* silent */ });
    }
  }, [product?.category_id]);

  const images = product ? getProductImages(product.media) : [];
  const price = Number(product?.price || 0);
  const promo = Boolean(product?.discount_enabled && product?.discount_price != null);
  const effectivePrice = promo ? Number(product?.discount_price) : price;
  const stock = product?.stock ?? null;
  const digital = isDigitalProduct(product);
  const outOfStock = !digital && stock != null && stock <= 0;
  const maxQty = stock != null && stock > 0 ? stock : 99;

  const handleAdd = () => {
    if (!product || outOfStock) return;
    addToCart({
      id: product.id,
      name: product.name,
      price: effectivePrice,
      original_price: promo ? price : null,
      media: images[0] || '',
      stock: digital ? 9999 : (stock ?? 9999),
      product_type: product.product_type || 'physical',
      digital_file_url: product.digital_file_url,
    });
    trackAddToCart({
      content_ids: [String(product.id)],
      content_name: product.name,
      value: effectivePrice * quantity,
      currency: monaie,
      num_items: quantity,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handleBuyNow = () => {
    handleAdd();
    navigate('/checkout');
  };

  const whatsappUrl = product
    ? buildWhatsAppUrl(
        whatsapp.number,
        `Bonjour ${tenant?.nomcommerce || ''}, je souhaite commander : ${quantity} × ${product.name} — ${(effectivePrice * quantity).toLocaleString()} ${monaie}.`,
      )
    : '#';

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center px-4">
        <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
          <i className="ri-shopping-bag-line text-2xl text-foreground-400"></i>
        </div>
        <h2 className="text-xl font-bold font-heading text-foreground-950 mb-2">Produit introuvable</h2>
        <p className="text-sm text-foreground-500 mb-6">Ce produit n&apos;existe pas ou n&apos;est plus disponible.</p>
        <Link to="/products" className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-medium no-underline hover:bg-primary-600 transition-colors whitespace-nowrap">
          Voir les produits
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto py-6 md:py-10">
        {/* Fil d'ariane */}
        <nav className="flex items-center gap-1.5 text-xs text-foreground-400 mb-6 flex-wrap">
          <Link to="/" className="hover:text-foreground-700 no-underline whitespace-nowrap">Accueil</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/products" className="hover:text-foreground-700 no-underline whitespace-nowrap">Nos produits</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-foreground-600 truncate max-w-[200px]">{product.name}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12">
          {/* Galerie */}
          <div>
            <div className="aspect-square bg-background-100 rounded-xl overflow-hidden border border-background-200/70">
              {images[activeImage] ? (
                <img
                  src={images[activeImage]}
                  alt={product.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <i className="ri-shopping-bag-line text-4xl text-foreground-300"></i>
                </div>
              )}
            </div>
            {images.length > 1 && (
              <div className="flex items-center gap-3 mt-4 flex-wrap">
                {images.map((img, i) => (
                  <button
                    key={img}
                    type="button"
                    onClick={() => setActiveImage(i)}
                    className={`w-16 h-16 rounded-lg overflow-hidden border-2 cursor-pointer transition-colors ${
                      activeImage === i ? 'border-primary-500' : 'border-background-200/70 hover:border-background-300/60'
                    }`}
                  >
                    <img src={img} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Infos */}
          <div>
            {(categoryName || menuRoleLabel(product.menu_role)) && (
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {menuRoleLabel(product.menu_role) && (
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-accent-50 text-accent-700 text-xs font-medium">
                    <i className="ri-restaurant-2-line"></i>
                    {menuRoleLabel(product.menu_role)}
                  </span>
                )}
                {categoryName && (
                  <span className="inline-block px-2.5 py-0.5 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium">
                    {categoryName}
                  </span>
                )}
              </div>
            )}
            <div className="flex items-start justify-between gap-4 mb-3">
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950">
                {product.name}
              </h1>
              <FavoriteButton
                entityType="product"
                entityId={product.id}
                label={product.name}
                metadata={{
                  image: images[0] || null,
                  ville: product.ville || null,
                  price: effectivePrice,
                  currency: monaie,
                }}
                className="mt-1 flex-shrink-0"
              />
            </div>

            {/* Prix */}
            <div className="flex items-end gap-3 mb-1">
              <span className="text-2xl md:text-3xl font-bold text-primary-600">
                {display(effectivePrice, monaie)}
              </span>
              {promo && (
                <span className="text-base text-foreground-400 line-through mb-1">
                  {display(price, monaie)}
                </span>
              )}
              {promo && (
                <span className="px-2 py-0.5 bg-accent-500 text-background-50 rounded-full text-xs font-semibold mb-1">
                  Promo
                </span>
              )}
            </div>
            {original(effectivePrice, monaie) && (
              <p className="text-sm text-foreground-400 mb-4">
                {original(effectivePrice, monaie)}
              </p>
            )}

            {/* Stock / type de livraison */}
            <div className="mb-5">
              {digital ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-secondary-50 text-secondary-700 text-xs font-medium">
                  <i className="ri-download-cloud-2-line"></i> Produit digital — livré par fichier
                </span>
              ) : outOfStock ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-600 text-xs font-medium">
                  <i className="ri-close-circle-line"></i> Rupture de stock
                </span>
              ) : stock != null ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-medium">
                  <i className="ri-checkbox-circle-line"></i> En stock ({stock} disponibles)
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-accent-50 text-accent-700 text-xs font-medium">
                  <i className="ri-checkbox-circle-line"></i> Disponible
                </span>
              )}
            </div>

            {product.description && (
              <p className="text-sm text-foreground-600 leading-relaxed mb-6 whitespace-pre-line">
                {product.description}
              </p>
            )}

            {/* Quantité (produits physiques uniquement) */}
            {!outOfStock && !digital && (
              <div className="flex items-center gap-4 mb-6">
                <span className="text-sm font-medium text-foreground-700">Quantité</span>
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                    className="w-9 h-9 rounded-full bg-background-100 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 transition-colors cursor-pointer"
                    aria-label="Diminuer"
                  >
                    <i className="ri-subtract-line text-sm"></i>
                  </button>
                  <span className="w-10 text-center text-sm font-semibold text-foreground-900 tabular-nums">{quantity}</span>
                  <button
                    type="button"
                    onClick={() => setQuantity((q) => Math.min(maxQty, q + 1))}
                    disabled={quantity >= maxQty}
                    className="w-9 h-9 rounded-full bg-background-100 border border-background-200/70 flex items-center justify-center text-foreground-500 hover:bg-background-200/70 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
                    aria-label="Augmenter"
                  >
                    <i className="ri-add-line text-sm"></i>
                  </button>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              <button
                type="button"
                onClick={handleBuyNow}
                disabled={outOfStock || !tenant}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold transition-colors whitespace-nowrap ${
                  outOfStock || !tenant
                    ? 'bg-background-100 text-foreground-400 cursor-not-allowed'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer'
                }`}
              >
                <i className="ri-flashlight-line"></i>
                {digital ? 'Obtenir maintenant' : 'Commander en ligne'}
              </button>

              <button
                type="button"
                onClick={handleAdd}
                disabled={outOfStock || !tenant}
                className={`w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-colors whitespace-nowrap ${
                  outOfStock || !tenant
                    ? 'bg-background-100 text-foreground-400 cursor-not-allowed'
                    : 'bg-background-100 text-foreground-800 hover:bg-background-200/70 cursor-pointer'
                }`}
              >
                {added ? (
                  <>
                    <i className="ri-check-line text-accent-600"></i>
                    Ajouté au panier
                  </>
                ) : (
                  <>
                    <i className="ri-shopping-cart-line"></i>
                    Ajouter au panier
                  </>
                )}
              </button>

              {whatsapp.enabled && (
                <a
                  href={whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-bold bg-green-500 text-white hover:bg-green-600 transition-colors cursor-pointer whitespace-nowrap no-underline"
                >
                  <i className="ri-whatsapp-line text-lg"></i>
                  Commander sur WhatsApp
                </a>
              )}
            </div>

            {/* Localisation */}
            {(product.ville || product.pays) && (
              <div className="mt-6 pt-5 border-t border-background-200/70 flex items-center gap-2 text-sm text-foreground-500">
                <i className="ri-map-pin-line text-primary-500"></i>
                <span>{[product.ville, product.pays].filter(Boolean).join(', ')}</span>
              </div>
            )}

            {digital && (
              <div className="mt-6 pt-5 border-t border-background-200/70 flex items-start gap-2 text-sm text-foreground-500">
                <i className="ri-information-line text-secondary-600 mt-0.5"></i>
                <span>
                  {product.digital_delivery_note || 'Aucune livraison physique : votre fichier sera mis à disposition immédiatement après votre commande.'}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="mt-10">
          <ReviewsSection
            organizationId={organizationId}
            entityType="product"
            entityId={product.id}
            title="Avis sur ce produit"
            emptyLabel="Aucun avis sur ce produit pour le moment. Soyez le premier à donner votre avis."
          />
        </div>

        <RecommendedSection
          kind="product"
          excludeId={product.id}
          tenantId={product.idcommerce ?? (isTenant && tenant ? Number(tenant.id) : null)}
          currency={monaie}
        />
      </div>
    </div>
  );
}